<template>
  <div class="cdh">
    <!-- DETAILS LOADING -->
    <conversation-detail-loading v-if="isDataLoading" />
    <template v-else>
      <!-- HEADER TOP BAR -->
      <div class="cdh__topbar">
        <div v-if="$screen.isTabletOrDesktop($mq)" class="cdh__topbar--mailbox-title">
          <i class="ddi ddi-mailbox" />
          <router-link
            :to="RouteAssistant.getMailboxLandingRoute($route.params.accountId, mailboxID)"
            class="just-a-link"
          >{{ mailboxName }}</router-link>
        </div>
        <conversation-detail-meta-options
          :global-attachments="globalAttachments"
          :is-pinned="isPinned"
          :show-harvest-option="showHarvestOption"
          :spam-type="spamType"
          @show-harvest-modal="$emit('show-harvest-modal')"
          @move-clicked="$emit('move-clicked')"
          @delete-clicked="$emit('delete-clicked')"
          @toggle-spam="$emit('toggle-spam')"
          @pin-toggled="$emit('pin-toggled')"
        />
      </div>
      <!-- SPAM HEADER -->
      <spam-header
        :is-toggling-spam="isTogglingSpam"
        :spam-type="spamType"
        allow-toggle
        @toggle-spam="handleToggleSpam"
      />
      <!-- FORM -->
      <div class="cdh__main">
        <div class="body">
          <!-- DESKTOP: META INFO HEADER -->
          <div v-if="$screen.isDesktop($mq)" class="desktop-meta-info-header">
            <div class="ref-number">
              #{{refNumber}}
            </div>
            <div>
              <priority-picker
                v-model:selected-id="internalPriorityID"
                :options="priorityOptions"
              />
            </div>
            <div>
              <due-date-picker v-model="internalDueDate" />
            </div>
          </div>
          <!-- SUBJECT -->
          <div prop="subject" class="subject-wrapper">
            <masthead-input
              ref="subject"
              v-model="internalSubject"
              :class="{'subject--focus': subjectIsFocus }"
              class="subject"
              placeholder="Give this a title…"
              maxlength="255"
              @focus="subjectIsFocus = true"
              @blur="subjectIsFocus = false"
              @input-submitted="updateSubject"
            />
          </div>
          <!-- DESKTOP: CUSTOM FIELDS -->
          <custom-field-values
            v-if="$screen.isDesktop($mq)"
            ref="customFieldValues"
            :entity-id="mailboxID"
            :item-id="conversationID"
            entity-type="mailbox"
            @value-updated="$emit('update-custom-field')"
          />
          <!-- CUSTOMER LINE -->
          <div class="customer-line">
            <!-- CUSTOMER PICKER -->
            <customer-picker
              :mailbox-id="mailboxID"
              :customer="customer"
              :other-conversations="otherConversations"
              @customer-name-updated="(name) => $emit('customer-name-updated', name)"
              @customer-updated="(email) => $emit('customer-updated', email)"
              @other-conversations-clicked="handleOtherConversationsClicked()"
            />
          </div>
          <!-- TABLET/MOBILE: META INFO HEADER -->
          <template v-if="$screen.isTabletOrMobile($mq)">
            <div class="tablet-mobile-meta-info-header">
              <div class="job-assignments">
                <!-- Jobs enabled -->
                <template v-if="hasJobs">
                  <div
                    v-for="(jobAppointee, index) in jobInfo.jobAppointees" 
                    :key="index" 
                    :class="{'selected': isJobAppointeeAssigned(jobAppointee)}"
                  >
                    <assignment-picker
                      v-model:selected-id="jobAppointeeMaps[index].appointeeID" 
                      :options="assembleAppointeeOptions(jobAppointee.appointee)"
                      :size="tabletMobilePickerSize"
                      :title="jobAppointee.job.name"
                    />
                  </div>
                </template>
              </div>
              <div class="meta-info">
                <!-- Assignee only -->
                <assignment-picker
                  v-if="!hasJobs"
                  v-model:selected-id="internalAssigneeID"
                  :options="assigneesOptions"
                  :size="tabletMobilePickerSize"
                  hide-name
                />
                <status-picker 
                  v-model:selected-id="internalStatusID" 
                  :statuses="statusOptions" 
                  :job-info="jobInfo" 
                  :size="tabletMobilePickerSize"
                />
                <priority-picker
                  v-model:selected-id="internalPriorityID" 
                  :options="priorityOptions"
                  :size="tabletMobilePickerSize"
                />
                <due-date-picker
                  v-model="internalDueDate"
                  :size="tabletMobilePickerSize"
                />
              </div>
            </div>
            <!-- TABLET OR MOBILE: CUSTOM FIELDS (sits above the first message) -->
            <custom-field-values
              ref="customFieldValues"
              :entity-id="mailboxID"
              :item-id="conversationID"
              entity-type="mailbox"
              class="tablet-mobile-custom-fields"
              @value-updated="$emit('update-custom-field')"
            />
            <!-- TABLET OR MOBILE: TAGS (sits below the custom fields) -->
            <div class="tablet-mobile-tags-holder">
              <tags-input
                v-model:selected-tags="internalListTags"
                :hide-icon="internalListTags.length > 0"
                :available-tags="tagsOptions"
              />
            </div>
            <div class="tablet-mobile-creator-line">
              {{ creationEvent.creator.name }} on
              {{ TimeAssistant.formatDateToDateAndTime(creationEvent.createdOn) }} wrote&hellip;
              <span v-if="creationEvent.isEdited">(Edited)</span>
            </div>
          </template>
          <!-- FIRST MESSAGE -->
          <conversation-detail-header-message
            v-if="!isDataLoading"
            :customer="customer"
            :creation-event="creationEvent"
            @update-incoming-message="(message) => $emit('update-incoming-message', message)"
          />       
          <!-- RELATED ITEMS -->
          <div id="relatedItemsDiv" class="related-items">
            <div class="related-items__nav">
              <div
                v-if="$permissions.isNotGuest"
                :class="{'selected' : selectedTaskType === 0}"
                @click="selectedTaskType=0"
              ><i class="ddi ddi-everything" />&nbsp;{{ checklistsButtonLabel }}</div>
              <div
                :class="{'selected' :  selectedTaskType === 1}"
                @click="selectedTaskType = 1"
              ><i class="ddi ddi-task" />&nbsp;{{ linkedTasksButtonLabel }}</div>
              <div
                v-if="hasOtherConversations"
                :class="{'selected' :  selectedTaskType === 2}"
                @click="selectedTaskType = 2"
              ><i class="ddi ddi-conversation" />&nbsp;{{ otherConversationsButtonLabel }}</div>
              <div>
                <checklist-picker
                  v-if="selectedTaskType === 0" 
                  :project-id="mailboxID" 
                  :item-id="conversationID" 
                  :is-linked-to-task="false"
                  @checklist-created="(checklist) => handleChecklistCreated(checklist)" 
                />
                <dd-icon-button v-if="selectedTaskType === 1" icon="task-add" type="circle" size="small" color="green" @click="openLinkTasksDrawer()"/>
              </div>
            </div>

            <!-- CHECKLISTS -->
            <checklist-items-list
              v-if="$permissions.isNotGuest"
              v-show="selectedTaskType === 0"
              :checklists="checklists"
              :is-linked-to-task="false"
              @checklist-deleted="(checklist) => handleChecklistDeleted(checklist)"
              @checklist-created="(checklist) => handleChecklistCreated(checklist)" 
            />

            <!-- LINKED TASKS -->
            <related-items-list
              v-show="selectedTaskType === 1"
              :items="linkedTasks"
              :entity-type="EEntities.PROJECTS"
              group-by-entity
              add-link-text="Add Linked Task"
              @add-link-clicked="openLinkTasksDrawer()"
              @item-clicked="(item) => selectLinkedTask(item.id)"
            />

            <!-- OTHER CONVERSATIONS -->
            <related-items-list
              v-show="selectedTaskType === 2"
              :items="otherConversations"
              :entity-type="EEntities.MAILBOXES"
              @item-clicked="(item) => selectOtherConversation(item.id)"
            />
          </div>
        </div>
        <!-- DESKTOP: SIDE OPTIONS -->
        <div v-if="$screen.isDesktop($mq)" class="options">
          <div>
            <i class="ddi ddi-workflows" />
            <status-picker
              v-model:selected-id="internalStatusID"
              :statuses="statusOptions"
              :job-info="jobInfo"
            />
          </div>
          <!-- Jobs enabled -->
          <div v-if="hasJobs" class="job-assignments">
            <i class="ddi ddi-unassigned" />
            <div
              v-for="(jobAppointee, index) in jobInfo.jobAppointees" 
              :key="index" 
              :class="{'selected': isJobAppointeeAssigned(jobAppointee)}"
            >
              <i v-if="isJobAppointeeAssigned(jobAppointee)" class="ddi ddi-check" />
              <span class="label">
                {{ jobAppointee.job.name }}
              </span>
              <assignment-picker
                v-model:selected-id="jobAppointeeMaps[index].appointeeID" 
                :options="assembleAppointeeOptions(jobAppointee.appointee)"
              />
            </div>
          </div>
          <!-- Assignee only -->
          <div v-else>
            <i class="ddi ddi-unassigned" />
            <assignment-picker
              v-model:selected-id="internalAssigneeID"
              :options="assigneesOptions"
            />
          </div>
          <!-- TAGS -->
          <div class="tags-holder">
            <i class="ddi ddi-tag" />
            <tags-input 
              v-model:selected-tags="internalListTags"
              :available-tags="tagsOptions" 
              hide-icon 
            />
          </div>
          <!-- FIRST MESSAGE HEADER -->
          <div class="creator-line">
            Started by {{ creationEvent.creator.name }}<br>
            {{ TimeAssistant.formatDateToDateAndTime(creationEvent.createdOn) }}
            <span v-if="creationEvent.isEdited">&mdash;&nbsp;Edited</span>
          </div>
        </div>
      </div>
    </template>

    <!-- LINK TASKS DRAWER -->
    <link-tasks-drawer
      v-model:show-drawer="showLinkTasksDrawer"
      :currently-linked-tasks="linkedTasks"
      :originating-conversation="conversationForLinking"
      :originating-item-event-id="originatingEventID"
      @task-created="$emit('linked-tasks-updated')"
      @selected-existing-task-id="(id) => linkExistingTask(id)"
    />

    <!-- TASK DETAIL DRAWER -->
    <task-detail-drawer
      v-model:show-drawer="showTaskDetailDrawer"
      :originating-item="conversationForLinking"
      :task="selectedLinkedTask"
      :prev-item="prevLinkedItem"
      :next-item="nextLinkedItem"
      @navigate="(item) => selectLinkedTask(item.itemID)"
      @unlink-task="unlinkSelectedLinkedTask"
    />

    <!-- CONVERSATION DETAIL DRAWER -->
    <conversation-detail-drawer
      v-model:show-drawer="showConversationDetailDrawer"
      :conversation="selectedOtherConversation"
      :prev-item="prevLinkedItem"
      :next-item="nextLinkedItem"
      @navigate="(item) => selectOtherConversation(item.itemID)"
    />
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { cloneDeep } from 'lodash-es'

/* Mixins */

/* Components */
import ConversationDetailLoading from './conversation-detail-loading.vue'
import ConversationDetailHeaderMessage from './conversation-detail-header-message.vue'
import ConversationDetailMetaOptions from './conversation-detail-meta-options.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import TypeAssistant from '@/helpers/type-assistant'
import TimeAssistant from '@/helpers/time-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities, SpamTypes } from '@/types'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IChecklist,
  IConvosOutputHistoryv12,
  IItemReference,
  IJobAppointee,
  ILinkedItemForListing,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'
import { IJobAppointeeMap, IJobInfo } from '@/types/data-objects/items'

/* API */
import ConversationsAPI from '@/api/modules/convos.api'
import { useAppStore } from '@/modules/app/app.store'

enum RelatedTaskTypes {
  Checklists = 0,
  Tasks = 1,
  Conversations = 2,
}

export default defineComponent({
  name: 'ConversationDetailHeader',

  components: {
    ConversationDetailLoading,
    ConversationDetailMetaOptions,
    ConversationDetailHeaderMessage,
  },

  props: {
    mailboxName: {
      type: String,
      required: true,
    },

    statusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },

    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },

    assigneesOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    tagsOptions: {
      type: Array as () => Array<string>,
      required: true,
    },

    checklists: {
      type: Array as () => IChecklist[],
      default: (): IChecklist[] => [],
    },

    linkedTasks: {
      type: Array as () => ILinkedItemForListing[],
      required: true,
    },

    otherConversations: {
      type: Array as () => ILinkedItemForListing[],
      required: true,
    },

    jobInfo: {
      type: Object as () => IJobInfo,
      default: null,
    },

    globalAttachments: {
      type: Array as () => IAttachment[],
      required: true,
    },

    refNumber: {
      type: Number,
      required: true,
    },

    creationDate: {
      type: [Date, String],
      required: true,
    },

    dueDate: {
      type: [Date, String],
      default: null,
    },

    customer: {
      type: Object as () => IPersonWithEmail,
      required: true,
    },

    assigneeID: {
      type: Number,
      default: null,
    },

    priorityID: {
      type: Number,
      required: true,
    },

    statusID: {
      type: Number,
      required: true,
    },

    subject: {
      type: String,
      required: true,
    },

    listTags: {
      type: Array as () => Array<string>,
      default: (): Array<string> => [],
    },

    showHarvestOption: {
      type: Boolean,
      required: true,
    },

    isDataLoading: {
      type: Boolean,
      default: false,
    },

    isPinned: {
      type: Boolean,
      required: true,
    },

    isTogglingSpam: {
      type: Boolean,
      default: false,
    },

    spamType: {
      type: Number,
      default: SpamTypes.NOT_SPAM,
    },

    creationEvent: {
      type: Object as () => IConvosOutputHistoryv12,
      required: true,
    },
  },

  emits: [
    'customer-name-updated',
    'customer-updated',
    'delete-clicked',
    'linked-tasks-updated',
    'move-clicked',
    'pin-toggled',
    'show-harvest-modal',
    'toggle-spam',
    'update-assignee',
    'update-custom-field',
    'update-dueDate',
    'update-incoming-message',
    'update-job-appointee',
    'update-priority',
    'update-status',
    'update-subject',
    'update-tags',
  ],

  data() {
    return {
      // Read once, not computed from $route, which has the next page's params by the time a late save finishes
      mailboxID: Number(this.$route.params.id),
      conversationID: Number(this.$route.params.childId),
      EEntities,
      ColorAssistant,
      TimeAssistant,
      SpamTypes,
      RouteAssistant,
      internalSubject: '',

      // UI controls
      subjectIsFocus: false,
      showModalHarvest: false,
      isUpdatingSubject: false,
      tabletMobilePickerSize: 'medium',
      selectedTaskType: RelatedTaskTypes.Tasks,

      // Link tasks drawer
      showLinkTasksDrawer: false,
      originatingEventID: null as any,

      // Task detail drawer
      showTaskDetailDrawer: false,
      selectedLinkedTask: {} as ILinkedItemForListing,

      // Convo detail drawer
      showConversationDetailDrawer: false,
      selectedOtherConversation: {} as ILinkedItemForListing,

      // Used to display previous/next links on drawers...
      nextLinkedItem: {} as ILinkedItemForListing,
      prevLinkedItem: {} as ILinkedItemForListing,

      // Job appointee data
      jobAppointeeMaps: [] as IJobAppointeeMap[],
      // Store to check differences on watching jobAppointeeMaps[i]
      // since old array is not tracked on inner mutations...
      previousJobAppointeeMaps: [] as IJobAppointeeMap[],
    }
  },

  computed: {
    ...mapState(useAppStore, ['itemEventForLinkedTaskID']),
    conversationForLinking(): IItemReference {
      return {
        id: this.conversationID,
        title: this.subject,
        refNumber: this.refNumber,
      } as IItemReference
    },
    hasOtherConversations(): boolean {
      return this.otherConversations.length > 0
    },
    otherConversationsButtonLabel(): string {
      return 'Other Conversations (' + this.otherConversations.length + ')'
    },
    checklistsButtonLabel(): string {
      return (
        'Checklists (' +
        this.checklists.reduce(
          (p, c) =>
            p +
            (c.checklistItems || []).reduce(
              (p2, c2) => p2 + (c2.isDone ? 1 : 0),
              0,
            ),
          0,
        ) +
        '/' +
        this.checklists.reduce(
          (p, c) => p + (c.checklistItems || []).length,
          0,
        ) +
        ')'
      )
    },
    linkedTasksButtonLabel(): string {
      return 'Linked Tasks (' + this.linkedTasks.length + ')'
    },
    hasSelectedTags(): boolean {
      return this.listTags.length > 0
    },
    hasJobs(): boolean {
      return this.jobInfo !== null
    },
    internalStatusID: {
      get(): any {
        return this.statusID
      },
      set(newValue: any) {
        if (this.statusID !== newValue) {
          this.$emit('update-status', newValue)
        }
      },
    },

    internalPriorityID: {
      get(): any {
        return this.priorityID
      },
      set(newValue: any) {
        if (this.priorityID !== newValue) {
          this.$emit('update-priority', newValue)
        }
      },
    },

    internalDueDate: {
      get(): any {
        return this.dueDate
      },
      set(newValue: any) {
        if (this.dueDate !== newValue) {
          this.$emit('update-dueDate', newValue)
        }
      },
    },

    internalAssigneeID: {
      get(): any {
        return this.assigneeID
      },
      set(newValue: any) {
        if (this.assigneeID !== newValue) {
          this.$emit('update-assignee', newValue)
        }
      },
    },

    internalListTags: {
      get(): any {
        return this.listTags
      },
      set(newValue: any) {
        this.$emit('update-tags', newValue)
      },
    },

    isSpam(): boolean {
      return TypeAssistant.isSpam(this.spamType)
    },
  },

  watch: {
    itemEventForLinkedTaskID: {
      handler(val) {
        if (val !== null) {
          this.openLinkTasksDrawer(val)
          useAppStore().itemEventForLinkedTaskID = null
        }
      },
    },

    subject: {
      immediate: true,
      handler(newVal: string) {
        this.internalSubject = newVal
        this.isUpdatingSubject = false
      },
    },

    'jobInfo.jobAppointees': {
      immediate: true,
      handler(newVal: IJobAppointee[]) {
        if (!newVal) {
          return
        }
        newVal.forEach(j => {
          this.jobAppointeeMaps.push({
            jobID: j.job.id,
            appointeeID: j.appointee !== null ? j.appointee.id : null,
          })
        })
        this.previousJobAppointeeMaps = cloneDeep(this.jobAppointeeMaps)
      },
    },

    jobAppointeeMaps: {
      async handler(newJobAppointeeMaps) {
        for (let i = 0; i < newJobAppointeeMaps.length; i++) {
          if (
            newJobAppointeeMaps[i].appointeeID !==
            this.previousJobAppointeeMaps[i].appointeeID
          ) {
            this.$emit(
              'update-job-appointee',
              newJobAppointeeMaps[i].jobID,
              newJobAppointeeMaps[i].appointeeID,
            )
          }
        }
        this.previousJobAppointeeMaps = cloneDeep(newJobAppointeeMaps)
      },
      deep: true,
    },

    checklists: {
      deep: 1,
      immediate: true,
      handler() {
        if (this.checklists.length > 0) {
          this.selectedTaskType = RelatedTaskTypes.Checklists
        }
      },
    },
  },

  methods: {
    handleOtherConversationsClicked() {
      this.selectedTaskType = 2
      document
        .getElementById('relatedItemsDiv')
        .scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    selectLinkedTask(taskID: number) {
      const itemIndex = this.linkedTasks.map(i => i.id).indexOf(taskID)
      this.selectedLinkedTask = this.linkedTasks[itemIndex]
      this.setNextPrevLinkedItems(this.linkedTasks, itemIndex)
      this.showTaskDetailDrawer = true
    },

    selectOtherConversation(conversationID: number) {
      const itemIndex = this.otherConversations
        .map(i => i.id)
        .indexOf(conversationID)
      this.selectedOtherConversation = this.otherConversations[itemIndex]
      this.setNextPrevLinkedItems(this.otherConversations, itemIndex)
      this.showConversationDetailDrawer = true
    },

    isJobAppointeeAssigned(jobAppointee: IJobAppointee): boolean {
      const mandate = this.jobInfo.assigneeMandates.find(
        am => am.statusID === this.statusID,
      )
      if (mandate) {
        const jobIDMandated = mandate.jobID

        if (jobAppointee.job.id !== jobIDMandated) {
          return false
        } else {
          if (
            (this.assigneeID === null && jobAppointee.appointee === null) ||
            (jobAppointee.appointee !== null &&
              this.assigneeID === jobAppointee.appointee.id)
          ) {
            return true
          }
        }
      }

      return false
    },

    // Ensures the job appointee is part of the list of options
    // (since we are simply pulling from the list of available assignees
    // it's possible the current job appointee was removed from the project
    // and never updated.)
    // May want to pull back a separate thing from getting listAssignees since
    // that is all memberships + current assignee -- so may be weird to see that.
    // Or just leave it. kinda edge casy.
    assembleAppointeeOptions(appointee: IPersonWithEmail): IPersonWithEmail[] {
      if (
        appointee === null ||
        this.assigneesOptions.find(a => a.id === appointee.id)
      ) {
        return this.assigneesOptions
      }
      return [...this.assigneesOptions, appointee]
    },

    async unlinkSelectedLinkedTask() {
      try {
        const response = await ConversationsAPI.put_UnlinkTask(
          this.mailboxID,
          this.conversationID,
          this.selectedLinkedTask.id,
        )

        this.$messaging.showSuccess(response.message)

        this.$emit('linked-tasks-updated', false)
        this.showTaskDetailDrawer = false
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async linkExistingTask(taskId: number) {
      try {
        const response = await ConversationsAPI.post_LinkExistingTasks(
          this.mailboxID,
          this.conversationID,
          [taskId],
        )

        this.$messaging.showSuccess(response.message)

        this.$emit('linked-tasks-updated', false)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async openLinkTasksDrawer(originatingEventID: number = null) {
      this.originatingEventID = originatingEventID
      this.showLinkTasksDrawer = true
    },

    async handleChecklistCreated(checklist: IChecklist) {
      this.checklists.push(checklist)
    },

    async handleChecklistDeleted(checklist: IChecklist) {
      this.checklists.splice(
        this.checklists.findIndex(i => i.id === checklist.id),
        1,
      )
    },

    // These are for the next/prev links on the drawer, not to be confused with the ones on the base header
    setNextPrevLinkedItems(
      linkedItems: ILinkedItemForListing[],
      itemIndex: number,
    ) {
      this.prevLinkedItem = null
      this.nextLinkedItem = null

      if (itemIndex > 0) {
        this.prevLinkedItem = linkedItems[itemIndex - 1]
      }

      if (itemIndex < linkedItems.length - 1) {
        this.nextLinkedItem = linkedItems[itemIndex + 1]
      }
    },

    handleToggleSpam() {
      this.$emit('toggle-spam')
    },

    updateSubject(newValue: string) {
      if (newValue.trim().length === 0) {
        this.internalSubject = this.subject
        return
      }
      if (this.subject !== newValue && !this.isUpdatingSubject) {
        this.isUpdatingSubject = true
        this.$emit('update-subject', newValue)
      }
    },
  },
})
</script>

<style lang="scss">
.cdh {
  &__header-spam-mobile {
    margin-top: 20px;
    margin-bottom: 10px;
  }

  &__topbar {
    position: relative;
    width: 100%;
    background-color: var(--color-background-green);
    border-radius: 8px 8px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    border-bottom: $border--dotted-light;

    @include media-breakpoint-down(sm) {
      padding: 0 $detail-gutter-pad-sm;
      background-color: transparent;
      border: none;
      justify-content: flex-end;
    }

    &--mailbox-title {
      @include lettering--p-sm;
      @include single-line-ellipsis;

      .ddi-mailbox {
        color: $color--green-80;
        padding-right: 8px;
      }
    }
  }

  &__main {
    display: flex;
    @include flex-row-horizontal-spacing(0.5 * $detail-gutter-pad-lg);
    margin-top: 30px;
    padding: 0 0 0 0.5 * $detail-gutter-pad-lg;

    .masthead-input {
      .el-textarea {
        &__inner {
          font-size: 24px !important;
        }
      }
    }

    @include media-breakpoint-down(md) {
      padding: 0 $detail-gutter-pad-md;
    }

    @include media-breakpoint-down(sm) {
      margin-top: 0;
      padding: 0 $detail-gutter-pad-sm;
    }

    .body {
      @include media-breakpoint-up(lg) {
        flex: 1;
      }

      width: 100%;
      // No wider than the space beside the options, whatever it holds (a wide table in the message scrolls or shrinks)
      min-width: 0;

      .desktop-meta-info-header {
        display: flex;
        @include flex-row-horizontal-spacing(10px);
        @include flex-row-vertical-spacing-on-wrap(10px);

        > .ref-number {
          @include lettering--h4;
          padding-top: 2px;

          @include media-breakpoint-down(sm) {
            padding-top: 4px;
          }
        }
      }

      .tablet-mobile-tags-holder {
        margin: 8px 0 0 -6px;
      }

      .tablet-mobile-custom-fields {
        margin-top: 10px;
      }

      .tablet-mobile-meta-info-header {
        margin-top: 10px;
        display: block;
        background-color: var(--color-background-primary);
        border: $border--solid-light;
        padding: 12px;
        border-radius: 8px;

        > .job-assignments {
          display: flex;
          justify-content: center;
          @include flex-row-horizontal-spacing(8px);
          @include flex-row-vertical-spacing-on-wrap(8px);
          margin-bottom: 8px;
        }

        > .meta-info {
          display: flex;
          justify-content: center;
          @include flex-row-horizontal-spacing(8px);
          @include flex-row-vertical-spacing-on-wrap(8px);
        }
      }

      .tablet-mobile-creator-line {
        @include lettering--p-sm;
        color: var(--color-text-primary);
        padding-top: 24px;
        /* Appropriate space for the more button on conversation-detail-header-message */
        margin-right: 40px;
      }

      .subject-wrapper {
        margin-bottom: 10px;

        .subject {
          width: calc(100% + 30px);
          margin-left: -15px;
          border: 6px solid transparent;

          > div {
            padding: 0 8px;

            .el-textarea__main {
              padding: 0;
            }
          }

          @include media-breakpoint-up(md) {
            &:hover {
              background-color: var(--color-background-secondary);
              border-radius: 8px;
            }
          }

          &--focus {
            width: calc(100% + 22px);
            margin-top: 4px;
            margin-left: -11px;
            border: solid 2px $projects--theme-color;
            border-radius: 8px;

            &:hover {
              background: var(--color-background-secondary);
              border-radius: 8px;
            }

            margin-bottom: 14px;
          }
        }
      }

      .customer-line {
        margin-top: 10px;
        display: flex;
        @include flex-row-horizontal-spacing(8px);
      }

      .related-items {
        margin-top: 20px;
        margin-bottom: 48px;

        &__nav {
          display: flex;
          padding: 0 1px;

          > div {
            padding: 8px 0 2px 0;
            margin: 0 12px;
            display: inline-block;
            cursor: pointer;
            @include lettering--p-sm;
            color: var(--color-text-third);
            font-weight: $bold;

            /* Add related items button */
            &:last-child {
              align-self: center;
              margin-left: auto !important;
              padding: 6px 0;
            }

            &.selected {
              color: var(--color-text-secondary);
              border-bottom: solid 2px $projects--theme-color;
            }
          }
        }
      }
    }

    .options {
      max-width: 240px;
      min-width: 240px;
      /* Aligns with bottom of first line of title */
      margin-top: 40px;
      margin-bottom: 40px;
      display: flex;
      flex-direction: column;

      $options-top-padding: 16px;

      > div {
        padding: $options-top-padding 10px;
        border-top: $border--dotted-light;
        position: relative;

        > .ddi {
          position: absolute;
          font-size: 14px;
          right: 15px;
          top: -12px;
          background-color: var(--color-background-header);
          padding: 4px;
          color: var(--color-text-third);
        }

        .label {
          display: block;
          @include lettering--p-sm;
          @include single-line-ellipsis;
          margin-bottom: 4px;
        }
      }

      > .tags-holder {
        .tags-input {
          margin-left: -6px;
          margin-top: -4px;
        }
      }

      > .job-assignments {
        padding: 0;
        padding: ($options-top-padding - 14px) 0;

        > div {
          position: relative;
          margin: 10px 0;
          padding: 7px 10px;

          &.selected {
            background-color: var(--color-background-primary);
            border: $border--dotted-light;
            border-right: none;
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;

            .label {
              font-weight: $bold;
            }
          }

          .ddi-check {
            position: absolute;
            top: 10px;
            right: 20px;
            font-size: 14px;
            color: $color--green;
          }
        }
      }

      .creator-line {
        @include lettering--p-sm;
        @include single-line-ellipsis;
        color: var(--color-text-primary);
        border-top: $border--dotted-light;
        padding-top: 14px;
      }
    }
  }
}
</style>
